import React, {ReactElement} from "react";
import {history, Nullable} from "@data-force/app";
import {useSelector} from "react-redux";
import {TranslateFunction} from "react-localize-redux";
import HistoryParams from "../../../interfaces/historyParams";
import {getHistoryParams} from "../../../utils/common";
import StoreState from "../../../interfaces/store/storeState";
import {CLICK_EVENT, ENTER_KEY, EnvMap} from "../../../utils/constants";
import logo from "../../../assets/img/logo/logo.svg";
import logoDev from "../../../assets/img/logo/logo-dev.svg";
import logoStg1 from "../../../assets/img/logo/logo-stg1.svg";
import logoStg2 from "../../../assets/img/logo/logo-stg2.svg";
import fontLogo from "../../../assets/img/logo/font-logo.svg";
import fontLogoDev from "../../../assets/img/logo/font-logo-dev.svg";
import fontLogoStg1 from "../../../assets/img/logo/font-logo-stg1.svg";
import fontLogoStg2 from "../../../assets/img/logo/font-logo-stg2.svg";
import "./Logo.scss";
import {selectTranslate} from "../../../store/selectors/translate";

interface Props {
    dark: boolean
}

const Logo: React.FC<Props> = ({dark}: Props): ReactElement => {
    const translatePath: string = "components.common.Logo.Logo";
    const translate: TranslateFunction = useSelector(selectTranslate);
    const env: string = useSelector(({serviceProperties}: StoreState) => serviceProperties.env);

    /**
     * Go to the initial view on logo click/enter events
     * @param {event: React.MouseEvent | React.KeyboardEvent} event
     */
    const goTo = (event: React.MouseEvent | React.KeyboardEvent): void => {
        const {key, type}: React.KeyboardEvent = event as React.KeyboardEvent;

        if (type === CLICK_EVENT || key === ENTER_KEY) {
            const historyParams: Nullable<HistoryParams> = getHistoryParams();
            history.replace(`/${historyParams?.idpId}/`);
        }
    };

    /**
     * Get src string for logo image based on current environment
     * @return {string}
     */
    const getSrc = (): string => {
        let src: string = dark ? logo : fontLogo;
        switch (env) {
            case EnvMap.DEV:
                src = dark ? logoDev : fontLogoDev;
                return src;
            case EnvMap.STG1:
                src = dark ? logoStg1 : fontLogoStg1;
                return src;
            case EnvMap.STG2:
                src = dark ? logoStg2 : fontLogoStg2;
                return src;
            default:
                return src;
        }
    };

    return (
        <div
            className="df-logo"
            role="button"
            tabIndex={0}
            onClick={goTo}
            onKeyDown={goTo}
        >
            <img
                className={`df-logo-image-${env || "default"}`}
                src={getSrc()}
                alt={translate(`${translatePath}.logoLabel.text`) as string}
            />
        </div>

    );
};

export default Logo;
